import { Box, Divider, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { RefetchIntervals } from "../../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { toDineroOrUndefined } from "../../../currency/currencyLib"
import { useMoney } from "../../../currency/useMoney"
import { LocalizedStrict } from "../../../localization/components/AppLocalized"
import { useDateTime } from "../../../localization/useDateTime"
import { useMerchant } from "../../../merchants/merchantHooks"

import type { MenusListResponse } from "../menuPlanning/menuPlanningApi"
import { fetchMenusList, MenuPlaningQuery } from "../menuPlanning/menuPlanningApi"
import type { DailyMenu, DishItem, Week } from "../menuPlanning/menuPlanningTypes"
import { SignageType } from "../menuPlanning/menuPlanningTypes"

const SKELETON_DAYS: ReadonlyArray<number> = [3, 2, 3]

const useStyles = makeStyles()((theme) => ({
    page: {
        minHeight: "100vh",
        width: "100%",
        background: theme.palette.common.white,
        overflowY: "auto",
        fontSize: "1.111vw",
    },
    content: {
        maxWidth: "75em",
        margin: "0 auto",
        padding: "4em 9em",
    },
    headerRow: {
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "1em",
    },
    title: {
        fontSize: "2.75em",
        color: theme.palette.tailwind.slate[500],
    },
    weekLabel: {
        fontSize: "1.5em",
        color: theme.palette.tailwind.slate[500],
    },
    headerDivider: {
        marginTop: "1.5em",
        borderColor: theme.palette.tailwind.slate[300],
    },
    daySection: {
        marginTop: "7.5em",
    },
    dayHeader: {
        display: "flex",
        alignItems: "baseline",
    },
    dayWeekday: {
        fontSize: "2em",
        color: theme.palette.tailwind.slate[800],
        fontWeight: 700,
        marginRight: "1em",
    },
    dayDate: {
        fontSize: "1.25em",
        color: theme.palette.tailwind.slate[500],
        fontWeight: 500,
    },
    dayDivider: {
        marginTop: "1em",
        borderColor: theme.palette.tailwind.slate[300],
    },
    productRow: {
        display: "flex",
        gap: "1.5em",
        padding: "2.75em 0",
        borderTop: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderBottom: `1px solid ${theme.palette.tailwind.slate[300]}`,
        "& + &": {
            marginTop: "-1px",
        },
    },
    productMain: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
    },
    productHeading: {
        display: "flex",
        alignItems: "center",
        gap: ".75em",
        flexWrap: "wrap",
        marginBottom: ".75em",
    },
    productName: {
        fontSize: "1.5em",
        color: theme.palette.tailwind.slate[800],
        fontWeight: 700,
    },
    productDescription: {
        fontSize: "1.25em",
        color: theme.palette.tailwind.slate[800],
    },
    allergens: {
        fontSize: "1em",
        color: theme.palette.tailwind.slate[500],
        fontWeight: 700,
        marginTop: ".75em",
    },
    allergensValue: {
        fontWeight: 400,
        marginLeft: ".25em",
    },
    productPrice: {
        fontSize: "1.5em",
        color: theme.palette.text.primary,
        whiteSpace: "nowrap",
    },
    tagChip: {
        display: "inline-flex",
        alignItems: "center",
        padding: ".125em .75em",
        borderRadius: ".25em",
        fontSize: "1.25em",
        background: theme.palette.tailwind.green[500],
        color: theme.palette.common.white,
        fontWeight: 500,
    },
    emptyMessage: {
        fontSize: "1.5em",
        color: theme.palette.tailwind.slate[500],
        textAlign: "center",
    },
    skeletonTitle: {
        fontSize: "2.75em",
        width: "10em",
    },
    skeletonWeekLabel: {
        fontSize: "1.5em",
        width: "5em",
    },
    skeletonWeekday: {
        fontSize: "2em",
        width: "6em",
        marginRight: "1em",
    },
    skeletonDayDate: {
        fontSize: "1.25em",
        width: "4em",
    },
    skeletonDishName: {
        fontSize: "1.5em",
        width: "18em",
        marginBottom: ".75em",
    },
    skeletonDishDescription: {
        fontSize: "1.25em",
        width: "30em",
    },
}))

// The endpoint returns every signage type; this screen only shows the menus published for the web.
const selectWebMenuWeeks = (data: MenusListResponse): Week[] =>
    (data?.menuPlanning?.weeks ?? [])
        .map((week) => ({
            ...week,
            dailyMenus: week.dailyMenus.filter(
                (dailyMenu) => dailyMenu.isActive && dailyMenu.signageTypes?.includes(SignageType.WEB_MENU),
            ),
        }))
        .filter((week) => week.dailyMenus.length > 0)

export function CanteenWeekMenuPage() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const dateTimeFactory = useDateTime()

    const today = dateTimeFactory.now()
    const from = today.startOf("week").toISODate() ?? undefined
    const to = today.endOf("week").toISODate() ?? undefined

    const menusResult = useApiQuery(fetchMenusList, {
        queryName: MenuPlaningQuery.LIST,
        dependencies: { merchantId, from, to, isActive: true },
        select: selectWebMenuWeeks,
        refetchInterval: RefetchIntervals.medium,
    })

    return (
        <Box className={classes.page}>
            <Box className={classes.content}>
                {menusResult.isInitialLoading ? <WeekMenuSkeleton /> : <WeekMenuContent weeks={menusResult.data} />}
            </Box>
        </Box>
    )
}

type WeekMenuContentProps = Readonly<{
    weeks: Week[] | undefined
}>

function WeekMenuContent({ weeks }: WeekMenuContentProps) {
    const { classes } = useStyles()

    if (!weeks) return null

    if (weeks.length === 0) {
        return (
            <LocalizedStrict id="public-canteen-week-menu-empty">
                <Typography className={classes.emptyMessage}>
                    Unfortunately, no menus have been created this week.
                </Typography>
            </LocalizedStrict>
        )
    }

    return (
        <>
            {weeks.map((week) => (
                <WeekMenuView key={week.startDate} week={week} />
            ))}
        </>
    )
}

function WeekMenuSkeleton() {
    const { classes } = useStyles()

    return (
        <>
            <Box className={classes.headerRow}>
                <Skeleton animation="wave" className={classes.skeletonTitle} />
                <Skeleton animation="wave" className={classes.skeletonWeekLabel} />
            </Box>
            <Divider className={classes.headerDivider} />

            {SKELETON_DAYS.map((dishCount, dayIndex) => (
                <Box className={classes.daySection} key={dayIndex}>
                    <Box className={classes.dayHeader}>
                        <Skeleton animation="wave" className={classes.skeletonWeekday} />
                        <Skeleton animation="wave" className={classes.skeletonDayDate} />
                    </Box>
                    <Divider className={classes.dayDivider} />
                    {Array.from({ length: dishCount }).map((_, dishIndex) => (
                        <Box className={classes.productRow} key={dishIndex}>
                            <Box className={classes.productMain}>
                                <Skeleton animation="wave" className={classes.skeletonDishName} />
                                <Skeleton animation="wave" className={classes.skeletonDishDescription} />
                            </Box>
                        </Box>
                    ))}
                </Box>
            ))}
        </>
    )
}

type WeekMenuViewProps = Readonly<{
    week: Week
}>

function WeekMenuView({ week }: WeekMenuViewProps) {
    const { classes } = useStyles()

    return (
        <>
            <Box className={classes.headerRow}>
                <LocalizedStrict id="public-canteen-week-menu-title">
                    <Typography className={classes.title}>Canteen menu</Typography>
                </LocalizedStrict>
                <LocalizedStrict id="public-canteen-week-menu-week-label" vars={{ weekNumber: week.weekInYear }}>
                    <Typography className={classes.weekLabel}>Week {week.weekInYear}</Typography>
                </LocalizedStrict>
            </Box>
            <Divider className={classes.headerDivider} />

            {week.dailyMenus.map((dailyMenu) => (
                <DaySection key={dailyMenu.id} dailyMenu={dailyMenu} />
            ))}
        </>
    )
}

type DaySectionProps = Readonly<{
    dailyMenu: DailyMenu
}>

function DaySection({ dailyMenu }: DaySectionProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const publishDate = dateTimeFactory.fromApi(dailyMenu.publishDate)

    return (
        <Box className={classes.daySection}>
            <Box className={classes.dayHeader}>
                <Typography className={classes.dayWeekday}>
                    {dateTimeFactory.formatCustom(publishDate, "cccc")}
                </Typography>
                <Typography className={classes.dayDate}>
                    {dateTimeFactory.formatCustom(publishDate, "d MMM")}
                </Typography>
            </Box>
            <Divider className={classes.dayDivider} />
            {dailyMenu.dishes.map((dish, index) => (
                <DishRow key={`${dish.name}-${index}`} dish={dish} />
            ))}
        </Box>
    )
}

type DishRowProps = Readonly<{
    dish: DishItem
}>

function DishRow({ dish }: DishRowProps) {
    const { classes } = useStyles()
    const moneyFactory = useMoney({})
    const priceDinero = toDineroOrUndefined(dish.price)
    const marketingTag = dish.productDescription?.marketingTag
    const allergenNames = dish.productDescription?.allergens.map((allergen) => allergen.name) ?? []

    return (
        <Box className={classes.productRow}>
            <Box className={classes.productMain}>
                <Box className={classes.productHeading}>
                    <Typography className={classes.productName}>{dish.name}</Typography>
                    {marketingTag && <Typography className={classes.tagChip}>{marketingTag}</Typography>}
                </Box>
                {dish.description && <Typography className={classes.productDescription}>{dish.description}</Typography>}
                {allergenNames.length > 0 && <AllergensLine allergens={allergenNames} />}
            </Box>
            {priceDinero && (
                <Typography className={classes.productPrice}>{moneyFactory.format(priceDinero)}</Typography>
            )}
        </Box>
    )
}

type AllergensLineProps = Readonly<{
    allergens: ReadonlyArray<string>
}>

function AllergensLine({ allergens }: AllergensLineProps) {
    const { classes } = useStyles()

    return (
        <Typography className={classes.allergens}>
            <LocalizedStrict id="public-canteen-week-menu-allergens-label">
                <span>Allergens:</span>
            </LocalizedStrict>
            <span className={classes.allergensValue}>{allergens.join(", ")}</span>
        </Typography>
    )
}
